import { enumFilterModes, enumMatchSubs } from "../constants";
import { isInReporting } from "../utils/isInReporting";
import { QFilter } from "./QFilter";
import { QRulePair } from "./QRulePair";
import { QXMLObject } from "./QXMLObject";
import { QFilterBlock } from "./QFilterBlock";
import { QRule } from "./QRule";
import { PositionFilterCondition } from "../utils/PositionFilterCondition";
import { QUserMgmtRFC } from "./QUserMgmtRFC";

export class QFilterMgr extends QXMLObject {
  constructor() {
    super();
    this.fParentClass = null;
    this.fFilterMode = enumFilterModes.remove;
    this.fFilterBlocks = null;
    this.fFilterList = null;
    this.fReportId = null;
    this.fFilterListeners = [];
    this.fUserModifiedFilters = false;
  }

  InitFilterMgr(parentClass) {
    this.fParentClass = parentClass;
    this.fFilterBlocks = {};
    this.fFilterList = [];
    const storedFilterMode = localStorage.getItem("fFilterMode");
    if (storedFilterMode !== null) {
      this.fFilterMode = parseInt(storedFilterMode);
    }
  }

  GetWSMgr() {
    var parentClass = this.fParentClass;
    if (parentClass) {
      var objMgr = parentClass.fObjMgr;
      if (objMgr.fClass) {
        var oClass = objMgr.fClass;
        if (oClass) return oClass.fWSMGr;
      }
    }
    return null;
  }

  SetReportId(reportId) {
    this.fReportId = reportId;
  }

  GetContextId() {
    var wsMgr = this.GetWSMgr();
    if (wsMgr == null || wsMgr.GetDoc() == null) return "charts";

    if (isInReporting()) {
      return `reporting_${this.fReportId}`;
    }

    var chartID = wsMgr.GetDoc().GetChartID();
    var org = wsMgr.GetAccountName();
    var user = wsMgr.GetUserName();

    return org + "_" + user + "_" + chartID;
  }

  // Provides backwards compatibility w/ Interactive Filters
  LoadLegacyLocalFilters() {
    const legacyFilterGroupsStr = localStorage.getItem("filtergroup");
    if (legacyFilterGroupsStr === null) return false;

    localStorage.setItem("filtergroup_legacy", legacyFilterGroupsStr);
    localStorage.removeItem("filtergroup");

    const legacyFilterGroups = JSON.parse(legacyFilterGroupsStr);
    if (legacyFilterGroups.length === 0) return false;

    const wsMgr = this.GetWSMgr();
    const doc = wsMgr.GetDoc();
    const objDB = doc.GetObjDB();
    const boxClass = objDB.GetClassByName("Box");

    this.fFilterBlocks = {};
    legacyFilterGroups.forEach(group => {
      const filterBlock = new QFilterBlock();
      filterBlock.InitFilterBlock();
      filterBlock.SetContextId(group.id);
      for (const flatRule of group.filters) {
        const [fieldName, fieldValues] = flatRule;
        const fieldDef = boxClass.GetFieldDefByName(fieldName);
        const rule = new QRulePair();
        rule.InitRulePair(
          fieldName,
          fieldDef.GetFieldID(),
          fieldDef.GetFieldClassName(),
          "inlist",
          fieldValues
        );
        filterBlock.AddRule(rule);
      }

      const existingFilterBlocks = this.GetFilterBlocks(group.id);
      this.fFilterBlocks[group.id] = [...existingFilterBlocks, filterBlock];
    });

    const migratedFilters = Object.keys(this.fFilterBlocks).length > 0;
    if (migratedFilters) {
      this.StoreFilters();
    }

    return migratedFilters;
  }

  LoadLocalFilters() {
    if (this.LoadLegacyLocalFilters()) return;

    const userFilters = this.GetWSMgr()?.GetUserInfo()?.GetChartRules()?.filters;
    let data;

    if (userFilters) {
      data = userFilters;
    } else {
      // Local storage fallback
      const filterStr = localStorage.getItem("filters");
      data = JSON.parse(filterStr);
    }

    this.fFilterBlocks = {};

    const filterBlocks = data?.["filterBlocks"] ?? [];
    for (const flatBlock of filterBlocks) {
      const filterBlock = new QFilterBlock();
      filterBlock.InitFilterBlock();
      filterBlock.LoadFromJSON(flatBlock);

      const ctxId = filterBlock.GetContextId();
      const existingFilterBlocks = this.fFilterBlocks[ctxId] ?? [];
      this.fFilterBlocks[ctxId] = [...existingFilterBlocks, filterBlock];
    }
  }

  StoreFilters() {
    const flatObj = this.OutputToJSON();
    localStorage.setItem("filters", JSON.stringify(flatObj));
    this.PersistUserFilters(flatObj);
  }

  PersistUserFilters(flatObj) {
    if (globalThis?.["OC_DISABLED_FILTER_DB_PERSISTENCE"]) return;
    QUserMgmtRFC.UpdateChartRules(this.GetWSMgr(), { filters: flatObj });
  }

  IsUserModifiedFilters() {
    return this.fUserModifiedFilters;
  }

  GetFilterMode() {
    return this.fFilterMode;
  }

  SetFilterMode(filterMode) {
    const wsMgr = this.GetWSMgr();
    wsMgr?.BroadcastTopic("FilterModeChange", filterMode);
    this.fFilterMode = filterMode;
    this.fUserModifiedFilters = true;
  }

  SetFilterBlocks(ctxId, filterBlocks) {
    this.fFilterBlocks[ctxId] = filterBlocks;
    this.fUserModifiedFilters = true;
    const wsMgr = this.GetWSMgr();

    wsMgr?.BroadcastTopic(`FilterUpdate_${ctxId}`, filterBlocks);
  }

  GetFilterBlocks(contextId) {
    return this.fFilterBlocks[contextId] ?? [];
  }

  Marshall() {
    const ctxId = this.GetContextId();
    const personalBlocks = this.GetFilterBlocks(ctxId).filter(block => block.GetEnabled());
    const sharedBlocks = this.GetFilterBlocks(`shared_${ctxId}`).filter(block =>
      block.GetEnabled()
    );

    return [...personalBlocks, ...sharedBlocks].map(fb => fb.Marshall()).flat();
  }

  HasActiveSavedFilter() {
    return this.GetAllowedFilterList().some(f => f.GetFilterApplied());
  }

  HasActiveFilter() {
    const contextId = this.GetContextId();
    const personalBlocks = this.GetFilterBlocks(contextId);
    const sharedBlocks = this.GetFilterBlocks(`shared_${contextId}`);
    return [...personalBlocks, ...sharedBlocks].some(block => {
      return block.GetEnabled() && block.GetRules().length > 0;
    });
  }

  NumberOfActiveInterFilters() {
    const contextId = this.GetContextId();
    return this.GetFilterBlocks(contextId).filter(block => {
      return block.GetEnabled() && block.GetRules().length > 0;
    }).length;
  }

  NumberOfActiveSharedFilters() {
    const contextId = this.GetContextId();
    return this.GetFilterBlocks(`shared_${contextId}`).filter(block => {
      return block.GetEnabled() && block.GetRules().length > 0;
    }).length;
  }

  NumberOfActiveSavedFilters() {
    return this.fFilterList.filter(filter => !filter.fFilterOff).length;
  }

  TotalNumberOfActiveFilters() {
    return (
      this.NumberOfActiveInterFilters() +
      this.NumberOfActiveSharedFilters() +
      this.NumberOfActiveSavedFilters()
    );
  }

  HasSavedFiltersApplied() {
    var filters = this.fFilterList;
    if (!filters) return false;
    return filters.some(filter => filter.GetFilterApplied());
  }

  PrepareFilterContext() {
    const ctx = {};
    ctx.hasActiveFilter = this.HasActiveFilter();
    ctx.contextId = this.GetContextId();
    if (ctx.hasActiveFilter) {
      const personalBlocks = this.GetFilterBlocks(ctx.contextId).filter(b => b.GetEnabled());
      const sharedBlocks = this.GetFilterBlocks(`shared_${ctx.contextId}`).filter(b =>
        b.GetEnabled()
      );
      ctx.blocks = [...personalBlocks, ...sharedBlocks];
    } else {
      ctx.blocks = [];
    }

    const allowedFilters = this.GetAllowedFilterList();
    ctx.savedFilterRules = [];
    for (const aFilter of allowedFilters) {
      if (!aFilter.GetFilterApplied()) continue;
      const aRule = new QRule();
      aRule.InitRule(enumMatchSubs.matchonly, 0);
      aRule.SetIsComplex(true);
      aRule.SetComplexDef(aFilter.GetFilterDef());
      ctx.savedFilterRules.push(aRule);
    }
    ctx.hasSavedFilters = ctx.savedFilterRules.length > 0;

    return ctx;
  }

  EvalSavedFiltersCtx(dbObj, docRef, ctx) {
    for (const aRule of ctx.savedFilterRules) {
      if (aRule.MatchRule(dbObj, docRef)) return true;
    }
    return false;
  }

  EvalInteractiveFilterCtx(dbObj, docRef, ctx) {
    if (!ctx.hasActiveFilter) return false;
    return !ctx.blocks.every(block => block.Matches(dbObj, docRef));
  }

  EvalPositionsInteractiveFilterCtx(dbObj, docRef, ctx) {
    if (!ctx.hasActiveFilter) {
      return PositionFilterCondition.AllFalse(dbObj);
    }

    const results = [];
    for (const block of ctx.blocks) {
      results.push(block.PositionsMatches(dbObj, docRef));
    }

    if (results.length === 0) {
      return PositionFilterCondition.AllFalse(dbObj);
    }

    return results.reduce((acc, result) => {
      return acc.and(result);
    });
  }

  EvalSavedFilters(dbObj, docRef) {
    const allowedFilters = this.GetAllowedFilterList();

    for (const aFilter of allowedFilters) {
      if (!aFilter.GetFilterApplied()) {
        continue;
      }

      const aRule = new QRule();
      aRule.InitRule(enumMatchSubs.matchonly, 0);
      aRule.SetIsComplex(true);
      const complexDef = aFilter.GetFilterDef();
      aRule.SetComplexDef(complexDef);
      if (aRule.MatchRule(dbObj, docRef)) {
        return true;
      }
    }

    return false;
  }

  EvalInteractiveFilter(dbObj, docRef) {
    if (!this.HasActiveFilter()) {
      return false;
    }

    const contextId = this.GetContextId();
    const personalBlocks = this.GetFilterBlocks(contextId);
    const sharedBlocks = this.GetFilterBlocks(`shared_${contextId}`);
    const blocks = [...personalBlocks, ...sharedBlocks];

    const matchesFilter = blocks.every(block => {
      const isEnabled = block.GetEnabled();
      if (!isEnabled) {
        return true;
      }

      return block.Matches(dbObj, docRef);
    });

    // Interactive filters have inverse logic of saved filters
    // Objects that match the criteria, will remain in document
    return !matchesFilter;
  }

  EvalPositionsInteractiveFilter(dbObj, docRef) {
    if (!this.HasActiveFilter()) {
      return PositionFilterCondition.AllFalse(dbObj);
    }

    const contextId = this.GetContextId();
    const personalBlocks = this.GetFilterBlocks(contextId);
    const sharedBlocks = this.GetFilterBlocks(`shared_${contextId}`);
    const blocks = [...personalBlocks, ...sharedBlocks];
    const results = [];

    for (const block of blocks) {
      const isEnabled = block.GetEnabled();
      if (!isEnabled) {
        continue;
      }

      results.push(block.PositionsMatches(dbObj, docRef));
    }

    if (results.length === 0) {
      return PositionFilterCondition.AllFalse(dbObj);
    }

    return results.reduce((acc, result) => {
      return acc.and(result);
    });
  }

  GetNumFilters() {
    return this.fFilterList.length;
  }

  LoadFromJSON(json) {
    this.fFilterList = [];
    var filters = json?.["filters"] ?? [];
    for (var i = 0; i < filters.length; i++) {
      var indexFilter = filters[i];
      var newFilter = new QFilter();
      newFilter.DoInitFilter();
      newFilter.LoadFromJSON(indexFilter);
      this.AddFilter(newFilter);
    }
  }

  OutputToJSON(stream) {
    const fObj = {
      filters: []
    };

    const n = this.GetNumFilters();
    for (var i = 0; i < n; i++) {
      const indexFilter = this.GetIndexFilter(i);
      fObj["filters"].push(indexFilter.OutputToJSON(stream));
    }

    const flatFilterBlocks = Object.entries(this.fFilterBlocks).reduce((acc, [ctxId, blocks]) => {
      if (ctxId.includes("reporting_") || ctxId.startsWith("shared_")) {
        return acc;
      }

      const flatBlocks = blocks.map(block => block.OutputToJSON());

      return [...acc, ...flatBlocks];
    }, []);

    fObj["filterBlocks"] = flatFilterBlocks;

    return fObj;
  }

  // Load from storage
  LoadSubElements(objTree) {}

  LoadChildElements(objTree) {
    var childList = objTree.GetChildOfTypeList("filter");
    for (var i = 0; i < childList.length; i++) {
      var filterInfo = childList[i];
      var aFilter = new QFilter();
      aFilter.DoInitFilter();
      aFilter.LoadFromXML(filterInfo);
      this.fFilterList.push(aFilter);
    }
  }

  AddFilter(aFilter) {
    this.fFilterList.push(aFilter);
  }

  ResetFilterMgr() {
    this.fFilterList = [];
  }

  DisableAppliedFilters() {
    var appliedFilters = [];
    for (var filterIdx in this.fFilterList) {
      var aFilter = this.fFilterList[filterIdx];
      if (aFilter.fFilterOff !== true) appliedFilters.push(aFilter.fFilterName);
      aFilter.fFilterOff = true;
    }

    return appliedFilters;
  }

  DisableInteractiveFilters() {
    const activeFilterBlocks = [];

    const contextID = this.GetContextId();
    const personalBlocks = this.GetFilterBlocks(contextID);
    const sharedBlocks = this.GetFilterBlocks(`shared_${contextID}`);
    const filterBlocks = [...personalBlocks, ...sharedBlocks];
    for (const filterBlock of filterBlocks) {
      if (!filterBlock.GetEnabled()) {
        continue;
      }

      filterBlock.SetEnabled(false);
      activeFilterBlocks.push(filterBlock);
    }

    return activeFilterBlocks;
  }

  GetFilterList() {
    return this.fFilterList;
  }

  GetNumAllowedFilters() {
    return this.GetAllowedFilterList().length;
  }

  GetAllowedFilterList() {
    const filterList = this.GetFilterList();
    const wsMgr = this.GetWSMgr();
    const myGroup = wsMgr.GetCurrentUserGroup();
    const doc = wsMgr.GetDoc();
    const myChartID = doc.GetChartID();

    return filterList.filter(function (item) {
      return (
        !myGroup ||
        !myGroup["fAllowedFilters"] ||
        !myGroup["fAllowedFilters"][myChartID] ||
        myGroup["fAllowedFilters"][myChartID].indexOf(item.fFilterName) > -1
      );
    });
  }

  CloneFilterList() {
    var filterList = [];
    var n = this.GetNumFilters();
    for (var i = 0; i < n; i++) {
      var aFilter = this.GetIndexFilter(i);
      var cFilter = aFilter.Clone();
      filterList.push(cFilter);
    }

    return filterList;
  }

  FindNameMatch(aFilter, filterList) {
    var n = filterList.length;
    var filterName = aFilter.GetFilterName();
    for (var i = 0; i < n; i++) {
      var cFilter = filterList[i];
      var cName = cFilter.GetFilterName();
      if (cName == filterName) return cFilter;
    }

    return null;
  }

  GetNameFilter(name) {
    var filterList = this.fFilterList;
    for (var filterIdx in filterList) {
      var theFilter = filterList[filterIdx];
      if (theFilter.fFilterName === name) return theFilter;
    }
    return null;
  }

  GetFilterIndex(aFilter) {
    return this.fFilterList.indexOf(aFilter);
  }

  GetIndexFilter(aFilterIdx) {
    return this.fFilterList[aFilterIdx];
  }
}
